import styled from '@emotion/styled';
import Card from 'storybookConfig/stories/cells/Card';
import Heading from 'storybookConfig/stories/molecules/Heading';
import Body from 'storybookConfig/stories/molecules/Body';
import openAccessIcon from '../../images/open-access-icon.svg';
import hybridAccessIcon from '../../images/hybrid-access-icon.svg';
import curatedAccessIcon from '../../images/curated-access-icon.svg';

const IconContainer = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto 16px auto;
  display: flex;
  justify-content: center;
  align-items: center;
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const Title = styled(Heading)`
  text-align: center;
  margin-bottom: 8px;
`;

const Subtitle = styled(Body)`
  text-align: center;
  color: ${({ theme }) => theme.color.bodyTextSecondary};
  margin-bottom: 32px;
`;

const ContentWrapper = styled.div`
  padding: 0 32px 32px 32px;
  text-align: center;
  max-width: 640px;
  margin: 0 auto;
`;

const AccessCard = styled.div`
  background: #f3f6fa;
  border-radius: 16px;
  padding: 24px 32px;
  display: flex;
  align-items: center;
  width: 100%;
  margin-bottom: 20px;
  box-shadow: 0 1px 4px rgba(16, 30, 54, 0.04);
`;

const AccessIcon = styled.img`
  width: 48px;
  height: 48px;
  margin-right: 24px;
  flex-shrink: 0;
  display: block;
`;

const AccessText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const AccessTitle = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #222f3e;
  margin-bottom: 8px;
`;

const AccessSubtitle = styled.div`
  font-size: 15px;
  color: #5b6b7a;
  line-height: 1.5;
  strong {
    color: #222f3e;
    font-weight: 600;
  }
`;

const LearnMoreLink = styled.a`
  color: ${({ theme }) => theme.color.blue500};
  text-decoration: none;
  display: block;
  text-align: center;
  margin-top: 24px;
  font-size: 14px;
  &:hover {
    text-decoration: underline;
  }
`;

interface RetailerAccessPanelProps {
  iconSrc: string;
  iconAlt?: string;
}

const RetailerAccessPanel = ({ iconSrc, iconAlt }: RetailerAccessPanelProps) => (
  <Card>
    <ContentWrapper>
      <IconContainer>
        <img src={iconSrc} alt={iconAlt || 'Retailer Access'} />
      </IconContainer>
      <Title variant="Headings/H3">Retailer Access</Title>
      <Subtitle>Determine retailer&apos;s access to your products.</Subtitle>
      <div>
        <AccessCard>
          <AccessIcon src={openAccessIcon} alt="Open Access" />
          <AccessText>
            <AccessTitle>Open Access</AccessTitle>
            <AccessSubtitle>
              While Carro enables retailers to quickly add your products to their stores without
              requiring manual approval, it does mean <strong>relinquishing some control</strong>{' '}
              over who can sell your items.
            </AccessSubtitle>
          </AccessText>
        </AccessCard>
        <AccessCard>
          <AccessIcon src={hybridAccessIcon} alt="Hybrid Access" />
          <AccessText>
            <AccessTitle>Hybrid Access</AccessTitle>
            <AccessSubtitle>
              If you&apos;re looking to collaborate with retailers, it&apos;s essential to target
              those that achieve at least <strong>${'{Amount}'}</strong> in annual revenue. This
              threshold ensures that you&apos;re partnering with businesses that have a solid
              financial foundation.
            </AccessSubtitle>
          </AccessText>
        </AccessCard>
        <AccessCard style={{ marginBottom: 0 }}>
          <AccessIcon src={curatedAccessIcon} alt="Curated Access" />
          <AccessText>
            <AccessTitle>Curated Access</AccessTitle>
            <AccessSubtitle>
              This option allows you to maintain greater control over your brand while collaborating
              with select retailers. However, it does require you to{' '}
              <strong>manage more tasks</strong>, as retailers will need to send connection requests
              that <strong>you need to review and approve</strong> before they add your products.
            </AccessSubtitle>
          </AccessText>
        </AccessCard>
      </div>
      <LearnMoreLink href="#">Learn more about the implications of this choice.</LearnMoreLink>
    </ContentWrapper>
  </Card>
);

export default RetailerAccessPanel;
